import React, { useMemo, useState, useEffect, useRef } from 'react'
import FeedbackPanel from './FeedbackPanel'
import axios from 'axios'
import toaster from '../Utils/toaster'
import CallDocumentsPanel, { CallOpiDocument } from './CallDocumentsPanel'
import { useAWSConnect } from '../app/context/ConnectStreamsContext'
import { useAuth } from '../app/modules/auth'
import { useIncomingCall } from '../hooks/useIncomingCall'
import { useSetAgentStatusMutation } from '../app/redux/agentStatusApi'
import { getCallGuardrail } from '../services/preCallGuardrailStorage'
import { useWidgetDockSide } from './ConnectStreamsWidget/widgetDockPreference'
import './ActiveCallModal.css'

const API_URL = process.env.REACT_APP_API_URL
const PRE_CALL_REMINDER_INITIAL_MS = 60_000
const PRE_CALL_REMINDER_INTERVAL_MS = 120_000

export interface PreCallQAItem {
  code: string
  question: string
  answer: string
}

function normalizeQaItem(item: any): PreCallQAItem {
  return {
    code: item?.code ?? item?.Code ?? '',
    question: item?.question ?? item?.Question ?? '',
    answer: item?.answer ?? item?.Answer ?? '',
  }
}

function fingerprintPreCallAnswers(items: PreCallQAItem[]): string {
  return items.map((item) => `${item.code}\u0000${item.answer}`).join('\n')
}

interface CallData {
  callId: string
  action: string
  caller: string
  language: string
  channel: string
  type: string
  timestamp: string
  callHeaderId: string
  preCallQA?: PreCallQAItem[]
  isClosed?: boolean
  showPreCallQuestionsAlert?: boolean
  /** false = opened from the local CCP connect; backend call:connected details still pending. */
  isEnriched?: boolean
  [key: string]: any // Allow additional fields from Firebase
}

interface ActiveCallModalProps {
  callData: CallData
  onClose: () => void
}

export const ActiveCallModal: React.FC<ActiveCallModalProps> = ({
  callData,
  onClose,
}) => {
  const { isMasterTab, isConnected, setAgentAvailable, setAgentOffline } = useAWSConnect()
  const { currentUser } = useAuth()
  const {
    registerCallGuardrail,
    updateCallGuardrailState,
    registerPreCallSubmitFailure,
  } = useIncomingCall()
  const [setAgentStatus] = useSetAgentStatusMutation()
  const isInterpreter = currentUser?.result?.userType === 'INTERPRETER'
  const isHidden = isInterpreter && isMasterTab !== true
  const dockSide = useWidgetDockSide()
  // Opened from the local CCP connect before the backend call:connected payload arrived.
  // Pre-call questions are only decided once it does (or useIncomingCall gives up waiting),
  // so an early "no questions" can never mark pre-call as submitted.
  const isAwaitingEnrichment = callData.isEnriched === false
  const preCallQaSignature = (callData.preCallQA ?? [])
    .map((item: any) => item?.code ?? item?.Code ?? '')
    .join('|')

  const persistedGuardrail = getCallGuardrail(callData.callHeaderId)
  const [qaArray, setQaArray] = useState<PreCallQAItem[]>(
    persistedGuardrail?.qaDraft ?? []
  )
  const [note, setNote] = useState(persistedGuardrail?.note ?? '')
  const [isPreCallLoading, setIsPreCallLoading] = useState(false)
  const [isPreCallSaving, setIsPreCallSaving] = useState(false)
  const [showFeedback, setShowFeedback] = useState(false)
  const [preCallSubmitted, setPreCallSubmitted] = useState(
    persistedGuardrail?.preCallSubmitted ?? false
  )
  const [lastSubmittedFingerprint, setLastSubmittedFingerprint] = useState<string | null>(() => {
    if (persistedGuardrail?.preCallSubmitted && persistedGuardrail.qaDraft?.length) {
      return fingerprintPreCallAnswers(persistedGuardrail.qaDraft)
    }
    return null
  })
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(
    persistedGuardrail?.feedbackSubmitted ?? false
  )
  const [activeTab, setActiveTab] = useState<'call' | 'documents'>('call')
  const [opiDocuments, setOpiDocuments] = useState<CallOpiDocument[]>([])
  const [documentsLoading, setDocumentsLoading] = useState(false)
  const [showDocumentNotification, setShowDocumentNotification] = useState(false)
  const [showPreCallReminderPopup, setShowPreCallReminderPopup] = useState(false)
  const documentNotificationShownRef = useRef<Set<string>>(new Set())
  const preCallReminderIntervalRef = useRef<number | null>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const forcedOfflineCallRef = useRef<string | null>(null)
  const callPrefix =
    callData?.isAiInterpretation === true ||
    callData?.IsAiInterpretation === true ||
    callData?.isAIInterpretation === true
      ? 'AI'
      : 'OPI'

  // Initialize or fetch pre-call Q&A once per call header (avoid SignalR/API object churn resetting saved state)
  useEffect(() => {
    const persisted = getCallGuardrail(callData.callHeaderId)
    const applyQa = (items: PreCallQAItem[], nextNote = '') => {
      const normalized = items.map(normalizeQaItem)
      const merged = normalized.map((item) => {
        const draft = persisted?.qaDraft.find((candidate) => candidate.code === item.code)
        // Prefer any non-empty draft answer over empty server/SignalR payload on replay.
        if (draft?.answer?.trim()) return {...item, answer: draft.answer}
        return draft ? {...item, answer: draft.answer} : item
      })
      const hasPreCallQuestions = merged.length > 0
      const isSavedOnServer =
        hasPreCallQuestions && normalized.every((item) => Boolean(item.answer.trim()))
      const isSubmitted = persisted?.preCallSubmitted === true || isSavedOnServer
      const persistedNote = persisted?.note ?? nextNote

      setQaArray(merged)
      setNote(persistedNote)
      setPreCallSubmitted((prev) => prev || isSubmitted)
      if (isSubmitted) {
        setLastSubmittedFingerprint(fingerprintPreCallAnswers(merged))
      }
      setFeedbackSubmitted(persisted?.feedbackSubmitted ?? false)
      registerCallGuardrail(callData, {
        qaDraft: merged,
        note: persistedNote,
        hasPreCallQuestions,
        // Never mark a call with no questions (yet) as submitted: the flag is sticky in
        // guardrail storage, so questions that arrive later (late call:connected after the
        // enrichment fallback fetch) would be treated as already answered.
        preCallSubmitted: isSubmitted,
        feedbackSubmitted: persisted?.feedbackSubmitted ?? false,
        ...(callData.isClosed === true ? {isClosed: true} : {}),
      })
    }

    if (callData?.preCallQA?.length) {
      applyQa(callData.preCallQA)
      setIsPreCallLoading(false)
      return
    }
    if (isAwaitingEnrichment) {
      // Wait for call:connected; this effect re-runs when it lands (or when waiting times out).
      setIsPreCallLoading(true)
      return
    }
    const callHeaderId = callData?.callHeaderId
    const contactId = callData?.callId ?? callData?.contactId
    if (!callHeaderId && !contactId) return
    const candidates = [
      contactId,
      `${callPrefix}-${String(callHeaderId).padStart(6, '0')}`,
      callHeaderId,
    ].filter(Boolean) as string[]
    let cancelled = false
    const fetchQa = async () => {
      setIsPreCallLoading(true)
      for (const cid of candidates) {
        try {
          const res = await axios.get(`${API_URL}/PreCalls/questions-transaction/${cid}`)
          const data = res?.data?.data || null
          const qa = data?.QA ?? data?.qa
          if (qa?.length) {
            if (!cancelled) applyQa(qa, data?.Note ?? data?.note ?? '')
            break
          }
        } catch (_) { }
      }
      if (!cancelled) {
        if (!getCallGuardrail(callData.callHeaderId)?.hasPreCallQuestions) applyQa([])
        setIsPreCallLoading(false)
      }
    }
    fetchQa()
    return () => { cancelled = true }
    // Intentionally depend on stable call identity fields, not the whole callData object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    callData.callHeaderId,
    callData.callId,
    callPrefix,
    registerCallGuardrail,
    isAwaitingEnrichment,
    preCallQaSignature,
  ])

  // Keep closed flag in sync without re-fetching / resetting pre-call answers
  useEffect(() => {
    if (callData.isClosed === true) {
      updateCallGuardrailState(callData.callHeaderId, {isClosed: true})
    }
  }, [callData.isClosed, callData.callHeaderId, updateCallGuardrailState])

  // Log all call data received from Firebase
  React.useEffect(() => {
    console.log('📞 Active Call Data (Full Firebase Payload):', JSON.stringify(callData, null, 2))
    console.log('📞 Active Call Data Keys:', Object.keys(callData))
  }, [callData])

  // trigger feedback when call is ended
  useEffect(() => {
    if (callData.isClosed === true) {
      setShowFeedback(true)
    }
  }, [callData.isClosed])

  const hasPreCallQuestions = qaArray.length > 0
  const currentPreCallFingerprint = fingerprintPreCallAnswers(qaArray)
  const answersMatchLastSubmit =
    lastSubmittedFingerprint !== null &&
    currentPreCallFingerprint === lastSubmittedFingerprint
  const canSubmitPreCallAnswers =
    !isPreCallSaving &&
    !qaArray.some((item) => !item.answer.trim()) &&
    !answersMatchLastSubmit

  useEffect(() => {
    if (
      !isConnected ||
      callData.isClosed === true ||
      !hasPreCallQuestions ||
      preCallSubmitted ||
      isPreCallLoading
    ) {
      setShowPreCallReminderPopup(false)
      if (preCallReminderIntervalRef.current) {
        clearInterval(preCallReminderIntervalRef.current)
        preCallReminderIntervalRef.current = null
      }
      return
    }

    const showReminder = () => setShowPreCallReminderPopup(true)
    const initialTimer = window.setTimeout(() => {
      showReminder()
      preCallReminderIntervalRef.current = window.setInterval(
        showReminder,
        PRE_CALL_REMINDER_INTERVAL_MS
      )
    }, PRE_CALL_REMINDER_INITIAL_MS)

    return () => {
      window.clearTimeout(initialTimer)
      if (preCallReminderIntervalRef.current) {
        clearInterval(preCallReminderIntervalRef.current)
        preCallReminderIntervalRef.current = null
      }
    }
  }, [
    callData.callHeaderId,
    callData.isClosed,
    hasPreCallQuestions,
    isConnected,
    isPreCallLoading,
    preCallSubmitted,
  ])

  useEffect(() => {
    if (
      callData.isClosed !== true ||
      !hasPreCallQuestions ||
      preCallSubmitted ||
      forcedOfflineCallRef.current === callData.callHeaderId
    ) return

    forcedOfflineCallRef.current = callData.callHeaderId
    setAgentOffline()
    setAgentStatus({status: false})
      .unwrap()
      .catch((error) => console.error('Failed to sync forced offline status:', error))
  }, [
    callData.callHeaderId,
    callData.isClosed,
    hasPreCallQuestions,
    preCallSubmitted,
    setAgentOffline,
    setAgentStatus,
  ])

  useEffect(() => {
    const modal = modalRef.current
    if (!modal) return
    const focusableSelector =
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    const firstUnanswered = modal.querySelector<HTMLInputElement>(
      'input[data-pre-call-unanswered="true"]'
    )
    const firstFocusable = modal.querySelector<HTMLElement>(focusableSelector)
    ;(firstUnanswered ?? firstFocusable)?.focus()

    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      const focusable = Array.from(modal.querySelectorAll<HTMLElement>(focusableSelector))
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    modal.addEventListener('keydown', trapFocus)
    return () => modal.removeEventListener('keydown', trapFocus)
  }, [callData.callHeaderId, isPreCallLoading])

  useEffect(() => {
    const callHeaderId = callData?.callHeaderId
    if (!callHeaderId) {
      setOpiDocuments([])
      setShowDocumentNotification(false)
      return
    }

    let cancelled = false
    const fetchDocuments = async () => {
      setDocumentsLoading(true)
      try {
        const response = await axios.get(`${API_URL}/documents/opi-documents/call/${callHeaderId}`)
        const documents = response?.data?.data || []
        if (!cancelled) {
          setOpiDocuments(documents)
          const notificationKey = String(callHeaderId)
          if (documents.length > 0 && !documentNotificationShownRef.current.has(notificationKey)) {
            setShowDocumentNotification(true)
            documentNotificationShownRef.current.add(notificationKey)
          } else if (!documents.length) {
            setShowDocumentNotification(false)
          }
        }
      } catch {
        if (!cancelled) {
          setOpiDocuments([])
          setShowDocumentNotification(false)
        }
      } finally {
        if (!cancelled) {
          setDocumentsLoading(false)
        }
      }
    }

    fetchDocuments()
    return () => {
      cancelled = true
    }
  }, [callData.callHeaderId])

  const caller = useMemo<{
    requester: string
    company: string
    firstName?: string
    lastName?: string
    companyName?: string
  }>(() => {
    if (callData?.caller) {
      try {
        const caller = JSON.parse(callData.caller)
        return {
          requester: `${caller.firstName || ''} ${caller.lastName || ''}`.trim(),
          company: caller.companyName || 'N/A',
          firstName: caller.firstName || '',
          lastName: caller.lastName || '',
          companyName: caller.companyName || 'N/A',
        }
      } catch (error) {
        console.error('Error parsing caller data:', error)
      }
    }

    return {
      requester: 'N/A',
      company: 'N/A',
    }
  }, [callData])

  // Skeletons for whatever the backend has not filled in yet.
  const isCallerPending =
    isAwaitingEnrichment && (!caller.company || caller.company === 'N/A')
  const isLanguagePending = isAwaitingEnrichment && !callData.language
  const isChannelPending = isAwaitingEnrichment && !callData.channel

  const handleCloseAttempt = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();

    if (hasPreCallQuestions && !preCallSubmitted) {
      toaster('error', 'Please save all pre-call answers before closing the call.');
    } else {
      // If we are showing feedback, we close after feedback submission or if user explicitly closes
      onClose();
    }
  };

  const updatePreCallAnswers = async () => {
    const hasUnansweredQuestions = qaArray.some((item) => !item.answer.trim())
    if (hasUnansweredQuestions) {
      toaster('error', 'Please answer all pre-call questions before saving.')
      return
    }
    // Backend expects Call_Id in prefixed format (prefix + numeric call header id), not contactId
    const callHeaderId = callData?.callHeaderId
    if (!callHeaderId) {
      toaster('error', 'Call header ID not found.')
      return
    }
    const callIdForApi = `${callPrefix}-${String(callHeaderId).padStart(6, '0')}`
    try {
      setIsPreCallSaving(true)
      const result = await axios.put(`${API_URL}/PreCalls/questions-transaction`, {
        Model: qaArray.map((x) => ({ code: x.code, answer: x.answer ?? '' })),
        Note: note,
        Call_Id: callIdForApi,
      })
      if (result?.data?.status === 'S') {
        const submittedFingerprint = fingerprintPreCallAnswers(qaArray)
        setLastSubmittedFingerprint(submittedFingerprint)
        setPreCallSubmitted(true)
        updateCallGuardrailState(callData.callHeaderId, {
          qaDraft: qaArray,
          note,
          hasPreCallQuestions: true,
          preCallSubmitted: true,
        })
        if (callData.isClosed === true) {
          setAgentAvailable()
          setAgentStatus({status: true})
            .unwrap()
            .catch((error) => console.error('Failed to restore available status:', error))
        }
        toaster('success', result?.data?.text ?? 'Pre-call answers saved.')
      } else {
        // Server rejected the submission — counts toward the release threshold so a
        // persistently failing endpoint cannot strand the interpreter offline indefinitely.
        registerPreCallSubmitFailure(String(callData.callHeaderId))
        toaster('error', result?.data?.text ?? 'Failed to save.')
      }
    } catch (err: any) {
      registerPreCallSubmitFailure(String(callData.callHeaderId))
      toaster('error', err?.response?.data?.message ?? err?.message ?? 'Failed to save pre-call answers.')
    } finally {
      setIsPreCallSaving(false)
    }
  }
  const hasOpiDocuments = opiDocuments.length > 0

  const handleReviewDocuments = () => {
    setShowDocumentNotification(false)
    setActiveTab('documents')
  }

  const handleDismissDocumentNotification = () => {
    setShowDocumentNotification(false)
  }

  const handleDismissPreCallReminder = () => {
    setShowPreCallReminderPopup(false)
  }

  const handleGoToPreCallQuestions = () => {
    setShowPreCallReminderPopup(false)
    setActiveTab('call')
    modalRef.current
      ?.querySelector<HTMLInputElement>('input[data-pre-call-unanswered="true"]')
      ?.focus()
  }

  if (isHidden) {
    return null
  }

  return (
    // Sit on the opposite side of the docked Connect widget so the two never overlap.
    <div className={`active-call-modal-backdrop widget-dock-${dockSide}`}>
      <div
        className='active-call-modal active-call-modal-maximized'
        ref={modalRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby='active-call-title'
      >
        <div className={`active-call-wrapper ${showFeedback ? 'with-feedback' : ''}`}>

          {/* Feedback Panel (Left on Desktop, Bottom on Mobile) */}
          {showFeedback && (
            <div className='feedback-panel-container'>
              <FeedbackPanel
                callHeaderId={callData.callHeaderId}
                onClose={() => handleCloseAttempt()}
                feedbackSubmitted={feedbackSubmitted}
                onFeedbackSubmitted={() => {
                  setFeedbackSubmitted(true)
                  updateCallGuardrailState(callData.callHeaderId, {
                    feedbackSubmitted: true,
                  })
                }}
              />
            </div>
          )}

          <div className='active-call-modal-content'>
            {/* Header with actions */}
            <div className='active-call-header'>
              <div className='active-call-title'>
                <i className='bi bi-telephone-fill me-2' style={{ color: "red" }}></i>
                <span id='active-call-title'>Active Call</span>
              </div>
              <div className=' ml-3 d-flex gap-2'>
                {callData.isClosed === true && (
                  <button
                    type='button'
                    className='btn btn-sm btn btn-xs  px-3 bg-danger ml-3'
                    onClick={handleCloseAttempt}
                    title='Close'
                    aria-label='Close active call'
                  >
                    <i className='bi bi-x-lg text-white' aria-hidden='true'></i>
                  </button>
                )}
              </div>
            </div>

            {/* Call Information */}
            <div className='active-call-body'>
              <ul className='nav nav-tabs nav-line-tabs mb-4 fs-7'>
                <li className='nav-item'>
                  <button
                    type='button'
                    className={`nav-link ${activeTab === 'call' ? 'active' : ''}`}
                    onClick={() => setActiveTab('call')}
                  >
                    Call Info
                  </button>
                </li>
                {hasOpiDocuments && (
                  <li className='nav-item'>
                    <button
                      type='button'
                      className={`nav-link ${activeTab === 'documents' ? 'active' : ''}`}
                      onClick={() => setActiveTab('documents')}
                    >
                      Documents / Scripts
                    </button>
                  </li>
                )}
              </ul>

              {activeTab === 'documents' && hasOpiDocuments && (
                <CallDocumentsPanel documents={opiDocuments} />
              )}

              {activeTab === 'call' && (
              <>
              {isAwaitingEnrichment && (
                <div className='active-call-enriching mb-3' role='status' aria-live='polite'>
                  <span className='spinner-border spinner-border-sm' aria-hidden='true' />
                  <span>Loading call details…</span>
                </div>
              )}
              <div className='d-flex align-items-start justify-content-around'>
                {isCallerPending ? (
                  <div
                    className='mb-3 d-flex flex-column align-items-center justify-content-center'
                    aria-busy='true'
                  >
                    <span className='visually-hidden'>Loading caller details…</span>
                    <div className='active-call-skeleton active-call-skeleton-avatar' aria-hidden='true' />
                    <div className='active-call-skeleton active-call-skeleton-title mt-3' aria-hidden='true' />
                    <div
                      className='active-call-skeleton active-call-skeleton-subtitle mt-2 mb-4'
                      aria-hidden='true'
                    />
                  </div>
                ) : (
                <div className='mb-3 d-flex flex-column align-items-center justify-content-center'>
                  <div className='active-call-avatar'>
                    <div className='active-call-avatar-icon'>
                      <span className='active-call-avatar-text'>
                        {(caller.company ?? 'N/A').charAt(0).toUpperCase() +
                          (caller.company ?? 'N/A').charAt(1).toUpperCase()}
                      </span>
                    </div>
                  </div>

                  <h4 className='text-primary fw-bold fs-1 mt-3'>{caller.company}</h4>
                  <h5 className='text-muted mb-4'>{caller.requester}</h5>
                </div>
                )}
                {/* Call Details */}
                <div className='active-call-details'>
                  <div className='row text-center'>
                    <div className='col-4'>
                      <div className='detail-item'>
                        <div className='icon-container'>
                          <i className='bi bi-translate text-muted mb-1'></i>
                        </div>
                        <div className='detail-item-content'>
                          <div className='small text-muted'>Language</div>
                          {isLanguagePending ? (
                            <div className='active-call-skeleton active-call-skeleton-value mx-auto' aria-label='Loading' />
                          ) : (
                            <div className='fw-bold'>{callData.language || 'N/A'}</div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className='col-4'>
                      <div className='detail-item'>
                        <div className='icon-container'>
                          <i className='bi bi-broadcast text-muted mb-1'></i>
                        </div>
                        <div className='detail-item-content'>
                          <div className='small text-muted'>Channel</div>
                          {isChannelPending ? (
                            <div className='active-call-skeleton active-call-skeleton-value mx-auto' aria-label='Loading' />
                          ) : (
                            <div className='fw-bold'>{callData.channel || 'N/A'}</div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className='col-4'>
                      <div className='detail-item'>
                        <div className='icon-container'>
                          <i className='bi bi-clock text-muted mb-1'></i>
                        </div>
                        <div className='detail-item-content'>
                          <div className='small text-muted'>Type</div>
                          <div className='fw-bold text-nowrap'>{callData.type === 'on_demand_call' ? 'On Demand' : (callData.type || 'N/A').replace('_', ' ')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>


              </div>

              {/* Call Information - Merged Call and Caller Data */}
              <div className='mt-4'>
                <div className='card border '>
                  <div className='card-body'>
                    <div className='row g-2'>
                      <div className='col-6'>
                        <small className='text-muted d-block'>Call Header ID</small>
                        <div className='fw-bold small'>{callData.callHeaderId || 'N/A'}</div>
                      </div>
                      <div className='col-6'>
                        <small className='text-muted d-block'>Action</small>
                        <div className='fw-bold small'>{callData.action || 'N/A'}</div>
                      </div>
                      <div className='col-6'>
                        <small className='text-muted d-block'>Timestamp</small>
                        <div className='fw-bold small'>
                          {callData.timestamp
                            ? new Date(callData.timestamp).toLocaleString()
                            : 'N/A'}
                        </div>
                      </div>
                      {/* Display caller information (excluding companyName and requester) */}
                      {callData.caller && Object.keys(caller)
                        .filter((key) => {
                          const value = caller[key as keyof typeof caller]
                          return value && !['companyName', 'requester', 'company', 'customerId'].includes(key)
                        })
                        .map((key) => (
                          <div key={key} className='col-6'>
                            <small className='text-muted d-block'>{key === 'firstName' ? 'Name' : key}</small>
                            <div className='fw-bold small'>{String(caller[key as keyof typeof caller] || 'N/A')}</div>
                          </div>
                        ))}
                      {/* Display any additional fields from Firebase */}
                      {Object.keys(callData)
                        .filter(
                          (key) =>
                            !['callId', 'callHeaderId', 'action', 'timestamp', 'caller', 'language', 'channel', 'type', 'preCallQA', 'contactId', 'isClosed', 'showPreCallQuestionsAlert', 'isEnriched'].includes(key)
                        )
                        .map((key) => (
                          <div key={key} className='col-6'>
                            <small className='text-muted d-block'>{key}</small>
                            <div className='fw-bold small'>
                              {typeof callData[key] === 'object'
                                ? JSON.stringify(callData[key])
                                : String(callData[key] || 'N/A')}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Pre-Call Questions and Answers */}
              <div className='separator my-3' />
              {hasPreCallQuestions && !preCallSubmitted && callData.isClosed === true && (
                <div className='pre-call-guardrail-alert mb-4' role='alert' aria-live='assertive'>
                  <i className='bi bi-exclamation-triangle-fill' aria-hidden='true'></i>
                  <div>
                    <h5>Pre-call questions required</h5>
                    <p>
                      This call has pre-call questions that need to be asked of the client.
                      If not filled, you won't be able to take further calls.
                    </p>
                  </div>
                </div>
              )}
              <div className='mb-4'>
                <h6 className='fs-6 fw-bold mb-3'>Pre-Call Questions and Answers</h6>
                {isPreCallLoading && (
                  <div className='active-call-skeleton-group' aria-busy='true'>
                    <span className='visually-hidden'>Loading pre-call questions…</span>
                    {[0, 1, 2].map((row) => (
                      <div key={row} className='row g-2 align-items-center mb-2' aria-hidden='true'>
                        <div className='col-sm-12 col-md-6'>
                          <div className='active-call-skeleton active-call-skeleton-line' />
                        </div>
                        <div className='col-sm-12 col-md-1' />
                        <div className='col-sm-12 col-md-5'>
                          <div className='active-call-skeleton active-call-skeleton-input' />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {!isPreCallLoading && qaArray.length > 0 && (
                  <div className='row g-2'>
                    {qaArray.map((qa, index) => (
                      <React.Fragment key={qa.code || index}>
                        <div className='col-sm-12 col-md-6'>
                          <label className='form-label fs-6 fw-bold '>
                            {index + 1}. {qa.question}
                          </label>
                        </div>
                        <div className='col-sm-12 col-md-1 d-flex align-items-center'>
                          <span className='text-dark fw-bold fs-6'>:</span>
                        </div>
                        <div className='col-sm-12 col-md-5'>
                          <input
                            type='text'
                            className='form-control form-control-sm'
                            placeholder='Enter answer'
                            value={qa.answer}
                            required
                            aria-required='true'
                            aria-label={qa.question}
                            aria-invalid={!qa.answer.trim()}
                            data-pre-call-unanswered={qa.answer.trim() ? undefined : 'true'}
                            onChange={(e) => {
                              const value = e.target.value ?? ''
                              setQaArray((prev) => {
                                const idx = prev.findIndex((i) => i.code === qa.code)
                                if (idx === -1) return prev
                                const next = [...prev]
                                next[idx] = { ...next[idx], answer: value }
                                const nextFingerprint = fingerprintPreCallAnswers(next)
                                const matchesLastSubmit =
                                  lastSubmittedFingerprint !== null &&
                                  nextFingerprint === lastSubmittedFingerprint
                                setPreCallSubmitted(matchesLastSubmit)
                                updateCallGuardrailState(callData.callHeaderId, {
                                  qaDraft: next,
                                  note,
                                  hasPreCallQuestions: true,
                                  preCallSubmitted: matchesLastSubmit,
                                })
                                return next
                              })
                            }}
                          />
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                )}
                {!isPreCallLoading && qaArray.length === 0 && (
                  <div className='text-muted small'>No pre-call questions for this call.</div>
                )}
                {qaArray.length > 0 && (
                  <div className='text-center mt-3'>
                    <button
                      type='button'
                      className='btn btn-sm btn-success rounded-pill'
                      onClick={updatePreCallAnswers}
                      disabled={!canSubmitPreCallAnswers}
                    >
                      {isPreCallSaving
                        ? 'Saving...'
                        : preCallSubmitted
                          ? 'Pre-call answers saved'
                          : 'Save pre-call answers'}
                    </button>
                  </div>
                )}
              </div>
              </>
              )}

              {documentsLoading && !hasOpiDocuments && (
                <div className='text-muted small'>Checking for client documents...</div>
              )}
            </div>

            {showDocumentNotification && hasOpiDocuments && (
              <div className='active-call-document-notification'>
                <div className='active-call-document-notification-card'>
                  <p className='mb-4'>
                    This client has documents/scripts available for this call. Please review.
                  </p>
                  <div className='d-flex justify-content-end gap-2'>
                    <button
                      type='button'
                      className='btn btn-light btn-sm'
                      onClick={handleDismissDocumentNotification}
                    >
                      Dismiss
                    </button>
                    <button
                      type='button'
                      className='btn btn-success btn-sm'
                      onClick={handleReviewDocuments}
                    >
                      Review
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showPreCallReminderPopup && hasPreCallQuestions && !preCallSubmitted && (
              <div className='active-call-document-notification'>
                <div className='active-call-document-notification-card'>
                  <p className='mb-4'>
                    This call has pre-call questions that need to be asked of the client.
                    If not filled, you won&apos;t be able to take further calls.
                  </p>
                  <div className='d-flex justify-content-end gap-2'>
                    <button
                      type='button'
                      className='btn btn-light btn-sm'
                      onClick={handleDismissPreCallReminder}
                    >
                      Dismiss
                    </button>
                    <button
                      type='button'
                      className='btn btn-success btn-sm'
                      onClick={handleGoToPreCallQuestions}
                    >
                      Complete now
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

